import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  Button,
  Menu,
  MenuItem,
  useMediaQuery,
  CircularProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import ProjectMgmtSummary from '../../components/ProjectAnalytics/ProjectMgmtSummary';
import GanttByProjectManager from '../../components/ProjectAnalytics/GanttByProjectManager';
import GanttByProcurementLead from '../../components/ProjectAnalytics/GanttByProcurementLead';
import GanttByEntity from '../../components/ProjectAnalytics/GanttByEntity';
import useFetchProjectTracker from '../../hooks/useFetchProjectTracker';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { ProjectAnalyticsTutorialVideo } from '../../config/tutorials';

const ProjectAnalytics = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);

  const isMobile = useMediaQuery('(max-width: 768px)');

  const { projects: rowData, loading, error } = useFetchProjectTracker();

  const computedData = useMemo(() => {
    if (!rowData) return { totalEfc: 0, totalBaseCost: 0, countOverBud: 0, totalProjects: 0 };

    return {
      totalEfc: rowData.reduce((sum, item) => sum + (item.efc || 0), 0),
      totalBaseCost: rowData.reduce((sum, item) => sum + (item.baseCost || 0), 0),
      countOverBud: rowData.filter((item) => (item.efcBudget || 0) > 100).length,
      totalProjects: rowData.length,
    };
  }, [rowData]);

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = (index) => {
    if (index !== null) setActiveTab(index);
    setAnchorEl(null);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Paper
        sx={{
          width: '100%',
          backgroundColor: 'white',
          border: '1px solid #ddd',
          padding: 3,
        }}
      >
        {loading ? (
          <Box sx={{ textAlign: 'center', padding: 3 }}>
            <CircularProgress />
            <Typography>Loading Project Data...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', padding: 3, color: 'red' }}>
            <Typography>Error: {error}</Typography>
          </Box>
        ) : (
          <>
            {isMobile ? (
              <>
                <Button variant="contained" onClick={handleOpenMenu} sx={{ width: '100%', marginBottom: 2 }}>
                  Filters
                </Button>
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => handleCloseMenu(null)}>
                  <MenuItem onClick={() => handleCloseMenu(0)}>Project Summary</MenuItem>
                  <MenuItem onClick={() => handleCloseMenu(1)}>Gantt By Entity</MenuItem>
                  <MenuItem onClick={() => handleCloseMenu(2)}>Gantt By Project Manager</MenuItem>
                  <MenuItem onClick={() => handleCloseMenu(3)}>Gantt By Procurement Lead</MenuItem>
                </Menu>
              </>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Tabs
                  value={activeTab}
                  onChange={handleChange}
                  indicatorColor="primary"
                  textColor="primary"
                  sx={{
                    '& .MuiTab-root': {
                      minWidth: '180px',
                      fontSize: '16px',
                      padding: '12px 20px',
                      textTransform: 'none',
                    },
                    '& .MuiTabs-indicator': {
                      height: '4px',
                      borderRadius: '2px',
                    },
                  }}
                >
                  <Tab label="Project Summary" />
                  <Tab label="Gantt By Entity" />
                  <Tab label="Gantt By Project Manager" />
                  <Tab label="Gantt By Procurement Lead" />
                </Tabs>

                {/* <Tooltip title="Watch Tutorial">
                  <IconButton
                    component="a"
                    href={ProjectAnalyticsTutorialVideo}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="primary"
                  >
                    <HelpOutlineIcon />
                  </IconButton>
                </Tooltip> */}
              </Box>
            )}

            <Box sx={{ padding: '0px' }}>
              {activeTab === 0 && (
                <ProjectMgmtSummary
                  totalProjects={computedData.totalProjects}
                  projectsOverBudget={computedData.countOverBud}
                  totalBaseCost={computedData.totalBaseCost}
                  totalEFC={computedData.totalEfc}
                />
              )}
              {activeTab === 1 && <GanttByEntity projects={rowData} />}
              {activeTab === 2 && <GanttByProjectManager projects={rowData} />}
              {activeTab === 3 && <GanttByProcurementLead projects={rowData} />}
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ProjectAnalytics;
